import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, RefreshCw, AlertTriangle, X, CheckCircle, FileText, Upload } from 'lucide-react';
import MainService from '../../services/MainService';
import { mapVehicleDropdownList, isApiSaveSuccess } from '../../utils/vehicleApiUtils';

const AddDocuments = () => {
  const { vehicleId, documentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [document, setDocument] = useState({
    Id: 0,
    VehicleId: vehicleId || '',
    DocumentType: '',
    DocumentNumber: '',
    IssueDate: '',
    ExpiryDate: '',
    IssuingAuthority: '',
    FilePath: '',
    File: null,
    Notes: ''
  });
  const [vehicles, setVehicles] = useState([]);
  const [filePreview, setFilePreview] = useState(null);

  useEffect(() => {
    loadVehicles();
    if (documentId) {
      loadDocument();
    }
  }, [documentId]);

  useEffect(() => {
    if (!documentId && vehicleId) {
      setDocument((prev) => ({ ...prev, VehicleId: vehicleId }));
    }
  }, [vehicleId, documentId]);

  const loadVehicles = async () => {
    try {
      const response = await MainService.VehiclesInDropDown();
      setVehicles(mapVehicleDropdownList(response));
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setVehicles([]);
    }
  };

  const loadDocument = async () => {
    try {
      setLoading(true);
      const row = await MainService.VehicleDocumentById(documentId);
      if (row) {
        setDocument({
          Id: row.Id ?? row.ID ?? 0,
          VehicleId: String(row.VehicleId ?? row.VehicleID ?? vehicleId ?? ''),
          DocumentType: row.DocumentType || '',
          DocumentNumber: row.DocumentNumber || '',
          IssueDate: row.IssueDate || '',
          ExpiryDate: row.ExpiryDate || '',
          IssuingAuthority: row.IssuingAuthority || '',
          FilePath: row.FilePath || '',
          File: null,
          Notes: row.Notes || ''
        });
      }
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDocument(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocument((prev) => ({
        ...prev,
        File: file,
        FilePath: file.name || prev.FilePath,
      }));
      setFilePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!document.DocumentType) {
      setError('Please enter document type');
      return;
    }
    if (!document.DocumentNumber) {
      setError('Please enter document number');
      return;
    }
    if (!document.IssueDate) {
      setError('Please select issue date');
      return;
    }

    const vid = Number(document.VehicleId || vehicleId);
    if (!vid) {
      setError('Please select vehicle');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const filePath =
        document.FilePath ||
        (document.File && document.File.name) ||
        '';
      const payload = {
        Id: Number(document.Id) || 0,
        VehicleId: vid,
        DocumentType: document.DocumentType,
        DocumentNumber: document.DocumentNumber,
        IssueDate: document.IssueDate,
        ExpiryDate: document.ExpiryDate || null,
        IssuingAuthority: document.IssuingAuthority || '',
        FilePath: filePath,
        Notes: document.Notes || ''
      };
      const response = await MainService.SaveVehicleDocument(payload);
      if (!isApiSaveSuccess(response)) {
        setError('Failed to save document');
        return;
      }
      setSuccess('Document saved successfully');
      setTimeout(() => {
        navigate(
          vehicleId
            ? `/home/vehicle-management/documents/${vehicleId}`
            : '/home/vehicle-management/documents'
        );
      }, 800);
    } catch (err) {
      console.error('Error saving document:', err);
      setError('Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDocument({
      Id: 0,
      VehicleId: vehicleId || '',
      DocumentType: '',
      DocumentNumber: '',
      IssueDate: '',
      ExpiryDate: '',
      IssuingAuthority: '',
      FilePath: '',
      File: null,
      Notes: ''
    });
    setFilePreview(null);
    setError('');
    setSuccess('');
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <FileText className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">
              {documentId ? 'Edit Document' : 'Add Document'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>Save</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 ml-8">
          {documentId ? 'Update document information' : 'Upload a new vehicle document'}
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Vehicle */}
          {!vehicleId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle <span className="text-red-500">*</span>
              </label>
              <select
                name="VehicleId"
                value={document.VehicleId}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
              >
                <option value="">Select Vehicle</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.Id} value={vehicle.Id}>
                    {vehicle.PlateNumber || vehicle.Number || vehicle.Name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Document Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="DocumentType"
              value={document.DocumentType}
              onChange={handleInputChange}
              placeholder="Enter document type"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Document Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="DocumentNumber"
              value={document.DocumentNumber}
              onChange={handleInputChange}
              placeholder="Enter document number"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Issue Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issue Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="IssueDate"
              value={document.IssueDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!document.IssueDate ? 'text-gray-400' : 'text-gray-900'}`}
            />
          </div>

          {/* Expiry Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
            <input
              type="date"
              name="ExpiryDate"
              value={document.ExpiryDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!document.ExpiryDate ? 'text-gray-400' : 'text-gray-900'}`}
            />
          </div>

          {/* Issuing Authority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Issuing Authority</label>
            <input
              type="text"
              name="IssuingAuthority"
              value={document.IssuingAuthority}
              onChange={handleInputChange}
              placeholder="Enter issuing authority"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File path or URL (optional if you upload a file below)
            </label>
            <input
              type="text"
              name="FilePath"
              value={document.FilePath}
              onChange={handleInputChange}
              placeholder="e.g. /uploads/doc.pdf or https://..."
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* File Upload */}
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Document File</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                className="hidden"
                id="fileInput"
              />
              <label htmlFor="fileInput" className="cursor-pointer">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {filePreview ? 'File selected' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, JPG, PNG (Max 10MB)</p>
              </label>
              {filePreview && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600">{document.File?.name}</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="Notes"
              value={document.Notes}
              onChange={handleInputChange}
              placeholder="Enter additional notes"
              rows="3"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDocuments;

